import { ReactNode } from "react";
import { Database } from "./database.types";
import { TemplateData } from "./template.types";
import { Component, DocumentComponent } from "./component.types";
import { TemplateInteractableLogicProps } from "./logic.types";
import { Slug } from "./util.types";

export enum PublishWebsiteActions {
  PublishToDefaultDomain = "publish_to_default_domain",
  PublishToCustomDomain = "publish_to_custom_domain",
  SaveAsOfflineDraft = "save_as_offline_draft",
}

export enum PopoverContentsTab {
  Values = "values",
  Styles = "styles",
  Mobile = "mobile",
}

export enum LeftSidebarInnerTab {
  Values = "values",
  Styles = "styles",
  Domain = "domain",
}

export interface AuthFormData {
  email: string;
  password: string;
  password2?: string;
}

export interface SaveWebsiteFormData {
  id: string;
  template_id: string;
  title: string;
  description: string;
  data: Record<string, any>; // Website data
}

export interface PublishWebsiteFormData extends SaveWebsiteFormData {
  action: PublishWebsiteActions;
  domain: string;
}

export interface UploadFileFormData {
  fileName: string;
  rawBase64: string;
  sizeLimitInMb?: number;
}

export interface UploadFileResponse {
  fileName: string;
  publicUrl: string;
}

export interface SaveComponentFormData {
  website_id: string;
  data: Record<string, any>;
  tags?: string[];
}

export enum Plan {
  Core = "core",
  Pro = "pro",
}

export interface PlanFeatureData {
  label: ReactNode;
  description?: ReactNode;
}

export interface PlanData {
  label: string;
  price: number; // measured in dollars/year
  features: PlanFeatureData[];
}

export interface UserMetaData {
  plan: Plan;
}

export interface TemplatePreview {
  id: string;
  template_id: string;
  image_bytea: string;
}

export enum RequestMethod {
  Get = "get",
  Post = "post",
}

export enum DNSRecordType {
  A = "a",
}

export interface ApiResponse<T> {
  status: "ok" | "error";
  result?: T;
  message?: string; // error message
}

export enum WebsiteStatus {
  Live = "live",
  Offline = "offline",
  Draft = "draft",
  Archived = "archived",
  Processing = "processing",
}

export interface Website {
  id: string;
  user_id: string;
  template_id: string;
  created_at: string;
  status: WebsiteStatus;
}

export type WebsiteRowInsert =
  Database["public"]["Tables"]["websites"]["Insert"];

export type WebsiteRow = Omit<
  Database["public"]["Tables"]["websites"]["Row"],
  "data"
> & { data: TemplateData };

export type TemplateRow = Omit<
  Database["public"]["Tables"]["templates"]["Row"],
  "data"
> & {
  data: TemplateData;
  component?: string;
};

export interface PreviewObject {
  template_id: number | null;
  website_id: number | null;
  url: string | null;
}

export type WebsiteType = WebsiteRow & {
  template?: { data: TemplateData };
};

export type RawWebsiteRowWithPreview = WebsiteRow & {
  preview: Database["public"]["Tables"]["previews"]["Row"]["image_bytea"];
};

export type ComponentRow = Omit<
  Database["public"]["Tables"]["components"]["Row"],
  "data" | "id"
> & { data: Component; id: number | string };

export enum Theme {
  Light = "light",
  Dark = "dark",
  System = "system",
}

export enum ViewMode {
  Edit = "edit",
  View = "view",
}

export interface SelectedDocumentAndIndex {
  document: DocumentComponent | null;
  index: number;
}

export interface BlogPost {
  slug: string;
  title: string;
  created: string;
  body: string;
  new: boolean;
}

export enum SidebarTab {
  General = "general",
  Pages = "pages",
  Components = "components",
}

export enum LibraryTab {
  Layout = "layout",
  Blocks = "blocks",
  Basic = "basic",
  Custom = "custom",
}

export interface DeployPayload {
  slug?: Slug;
  size?: number;
  rawFile: string;
  fileName: string;
}

export type PersistedNavbarInteractableId =
  TemplateInteractableLogicProps["interactableId"];
